// @ts-nocheck
/**
 * Shared Lucide icon catalog for template `icon` components.
 *
 * Host-agnostic data only — no React. Containd resolves `lucideName` via
 * `@tamagui/lucide-icons`; the template editor via `lucide-react`.
 */

export type IconCategory =
  | 'actions'
  | 'navigation'
  | 'status'
  | 'media'
  | 'objects'
  | 'system';

export interface IconCatalogEntry {
  /** Stable catalog id stored on IconComponent.icon.name (kebab-case). */
  readonly id: string;
  /** Lucide export name (PascalCase) shared by Tamagui and lucide-react. */
  readonly lucideName: string;
  /** Grouping for editor pickers. */
  readonly category: IconCategory;
  /** Optional search keywords. */
  readonly keywords?: readonly string[];
}

/**
 * Curated allowlist. Expand freely — schema stores opaque string ids;
 * unknown ids fail closed at resolve time.
 */
export const ICON_CATALOG: readonly IconCatalogEntry[] = [
  // Actions
  { id: 'plus', lucideName: 'Plus', category: 'actions', keywords: ['add', 'create'] },
  { id: 'plus-circle', lucideName: 'PlusCircle', category: 'actions', keywords: ['add'] },
  { id: 'minus', lucideName: 'Minus', category: 'actions' },
  { id: 'x', lucideName: 'X', category: 'actions', keywords: ['close', 'cancel'] },
  { id: 'check', lucideName: 'Check', category: 'actions', keywords: ['done', 'ok'] },
  { id: 'trash-2', lucideName: 'Trash2', category: 'actions', keywords: ['delete', 'remove'] },
  { id: 'edit', lucideName: 'Pencil', category: 'actions', keywords: ['pencil', 'write'] },
  { id: 'copy', lucideName: 'Copy', category: 'actions', keywords: ['duplicate'] },
  { id: 'cut', lucideName: 'Scissors', category: 'actions', keywords: ['scissors'] },
  { id: 'paste', lucideName: 'ClipboardPaste', category: 'actions', keywords: ['clipboard'] },
  { id: 'save', lucideName: 'Save', category: 'actions' },
  { id: 'download', lucideName: 'Download', category: 'actions' },
  { id: 'upload', lucideName: 'Upload', category: 'actions' },
  { id: 'share', lucideName: 'Share', category: 'actions' },
  { id: 'search', lucideName: 'Search', category: 'actions', keywords: ['find'] },
  { id: 'filter', lucideName: 'Filter', category: 'actions' },
  { id: 'refresh-cw', lucideName: 'RefreshCw', category: 'actions', keywords: ['reload', 'sync'] },
  { id: 'send', lucideName: 'Send', category: 'actions' },
  { id: 'external-link', lucideName: 'ExternalLink', category: 'actions', keywords: ['open'] },
  { id: 'link', lucideName: 'Link', category: 'actions' },

  // Navigation
  { id: 'home', lucideName: 'Home', category: 'navigation' },
  { id: 'menu', lucideName: 'Menu', category: 'navigation', keywords: ['hamburger'] },
  { id: 'chevron-left', lucideName: 'ChevronLeft', category: 'navigation', keywords: ['back'] },
  { id: 'chevron-right', lucideName: 'ChevronRight', category: 'navigation', keywords: ['forward'] },
  { id: 'chevron-up', lucideName: 'ChevronUp', category: 'navigation' },
  { id: 'chevron-down', lucideName: 'ChevronDown', category: 'navigation' },
  { id: 'arrow-left', lucideName: 'ArrowLeft', category: 'navigation' },
  { id: 'arrow-right', lucideName: 'ArrowRight', category: 'navigation' },
  { id: 'more-horizontal', lucideName: 'MoreHorizontal', category: 'navigation', keywords: ['ellipsis'] },
  { id: 'more-vertical', lucideName: 'MoreVertical', category: 'navigation' },

  // Status
  { id: 'info', lucideName: 'Info', category: 'status' },
  { id: 'alert-triangle', lucideName: 'AlertTriangle', category: 'status', keywords: ['warning'] },
  { id: 'alert-circle', lucideName: 'AlertCircle', category: 'status' },
  { id: 'check-circle', lucideName: 'CheckCircle', category: 'status', keywords: ['success'] },
  { id: 'x-circle', lucideName: 'XCircle', category: 'status', keywords: ['error'] },
  { id: 'bell', lucideName: 'Bell', category: 'status', keywords: ['notification'] },
  { id: 'star', lucideName: 'Star', category: 'status', keywords: ['favorite', 'rating'] },
  { id: 'heart', lucideName: 'Heart', category: 'status', keywords: ['like', 'favorite'] },
  { id: 'flag', lucideName: 'Flag', category: 'status' },
  { id: 'bookmark', lucideName: 'Bookmark', category: 'status' },
  { id: 'zap', lucideName: 'Zap', category: 'status', keywords: ['bolt', 'power'] },

  // Media
  { id: 'image', lucideName: 'Image', category: 'media', keywords: ['photo', 'picture'] },
  { id: 'camera', lucideName: 'Camera', category: 'media' },
  { id: 'video', lucideName: 'Video', category: 'media' },
  { id: 'mic', lucideName: 'Mic', category: 'media', keywords: ['microphone'] },
  { id: 'volume-2', lucideName: 'Volume2', category: 'media', keywords: ['sound', 'audio'] },
  { id: 'play', lucideName: 'Play', category: 'media' },
  { id: 'pause', lucideName: 'Pause', category: 'media' },
  { id: 'eye', lucideName: 'Eye', category: 'media', keywords: ['view', 'visible'] },
  { id: 'eye-off', lucideName: 'EyeOff', category: 'media', keywords: ['hidden'] },

  // Objects
  { id: 'file-text', lucideName: 'FileText', category: 'objects', keywords: ['document'] },
  { id: 'folder', lucideName: 'Folder', category: 'objects' },
  { id: 'archive', lucideName: 'Archive', category: 'objects' },
  { id: 'book-open', lucideName: 'BookOpen', category: 'objects', keywords: ['book'] },
  { id: 'tag', lucideName: 'Tag', category: 'objects', keywords: ['label'] },
  { id: 'list', lucideName: 'List', category: 'objects' },
  { id: 'calendar', lucideName: 'Calendar', category: 'objects', keywords: ['date'] },
  { id: 'clock', lucideName: 'Clock', category: 'objects', keywords: ['time'] },
  { id: 'map-pin', lucideName: 'MapPin', category: 'objects', keywords: ['location'] },
  { id: 'mail', lucideName: 'Mail', category: 'objects', keywords: ['email'] },
  { id: 'phone', lucideName: 'Phone', category: 'objects' },
  { id: 'message-square', lucideName: 'MessageSquare', category: 'objects', keywords: ['chat'] },
  { id: 'users', lucideName: 'Users', category: 'objects', keywords: ['people'] },
  { id: 'circle-user', lucideName: 'CircleUser', category: 'objects', keywords: ['profile', 'avatar'] },
  { id: 'gift', lucideName: 'Gift', category: 'objects' },
  { id: 'shopping-cart', lucideName: 'ShoppingCart', category: 'objects', keywords: ['cart'] },
  { id: 'credit-card', lucideName: 'CreditCard', category: 'objects', keywords: ['payment'] },
  { id: 'award', lucideName: 'Award', category: 'objects', keywords: ['badge', 'trophy'] },
  { id: 'tree-pine', lucideName: 'TreePine', category: 'objects', keywords: ['tree', 'nature'] },
  { id: 'stamp', lucideName: 'Stamp', category: 'objects', keywords: ['template'] },

  // System
  { id: 'settings', lucideName: 'Settings', category: 'system', keywords: ['gear', 'cog'] },
  { id: 'database', lucideName: 'Database', category: 'system' },
  { id: 'hard-drive', lucideName: 'HardDrive', category: 'system', keywords: ['storage', 'disk'] },
  { id: 'cloud', lucideName: 'Cloud', category: 'system', keywords: ['sync'] },
  { id: 'wifi', lucideName: 'Wifi', category: 'system' },
  { id: 'lock', lucideName: 'Lock', category: 'system', keywords: ['secure'] },
  { id: 'unlock', lucideName: 'Unlock', category: 'system' },
  { id: 'shield', lucideName: 'Shield', category: 'system', keywords: ['security'] },
  { id: 'shield-check', lucideName: 'ShieldCheck', category: 'system', keywords: ['shield-user', 'verified'] },
  { id: 'fingerprint', lucideName: 'Fingerprint', category: 'system', keywords: ['biometric'] },
  { id: 'globe', lucideName: 'Globe', category: 'system', keywords: ['world', 'web'] },
  { id: 'sun', lucideName: 'Sun', category: 'system', keywords: ['light', 'theme'] },
  { id: 'bar-chart-3', lucideName: 'BarChart3', category: 'system', keywords: ['chart', 'stats'] },
  { id: 'hash', lucideName: 'Hash', category: 'system', keywords: ['number'] },
  { id: 'at-sign', lucideName: 'AtSign', category: 'system' },
  { id: 'percent', lucideName: 'Percent', category: 'system' },
  { id: 'dollar-sign', lucideName: 'DollarSign', category: 'system', keywords: ['money'] },
  { id: 'smile', lucideName: 'Smile', category: 'system', keywords: ['emoji'] },
  { id: 'circle', lucideName: 'Circle', category: 'system', keywords: ['dot'] },
  { id: 'square', lucideName: 'Square', category: 'system' },
] as const;

/** All catalog ids as a readonly array. */
export const ICON_IDS: readonly string[] = ICON_CATALOG.map((entry) => entry.id);

const ICON_BY_ID = new Map<string, IconCatalogEntry>(
  ICON_CATALOG.map((entry) => [entry.id, entry]),
);

/** Default catalog id used when creating a new icon component. */
export const DEFAULT_ICON_ID = 'star';

/** Type guard: true when `value` is a known catalog id. */
export function isIconId(value: unknown): value is string {
  return typeof value === 'string' && ICON_BY_ID.has(value);
}

/** Lookup catalog metadata; returns undefined for unknown ids. */
export function getIconMeta(id: string): IconCatalogEntry | undefined {
  return ICON_BY_ID.get(id);
}

/** Lucide PascalCase export name for a catalog id, or undefined if unknown. */
export function getLucideName(id: string): string | undefined {
  return ICON_BY_ID.get(id)?.lucideName;
}

/** Filter catalog entries by free-text query (id, lucideName, keywords). */
export function searchIconCatalog(query: string): readonly IconCatalogEntry[] {
  const q = query.trim().toLowerCase();
  if (q.length === 0) return ICON_CATALOG;
  return ICON_CATALOG.filter((entry) => {
    if (entry.id.includes(q)) return true;
    if (entry.lucideName.toLowerCase().includes(q)) return true;
    if (entry.category.includes(q)) return true;
    return entry.keywords?.some((kw) => kw.includes(q)) ?? false;
  });
}
